// Shared chart palette + cross-page-stable color assignment.
//
// Distribution charts (enum pie/bar, map key pie/bar, map multi-line)
// show slices for an overlapping set of categories that changes from
// one time window to the next. We want a category's color to stay the
// same as the user pages between windows — even though the chart only
// knows about the current window's data and may not have ever seen the
// previous window's categories.
//
// Approach: each category's preferred palette slot is a stateless hash
// of its identity string. Two categories on the same page that hash to
// the same slot resolve via linear probing in caller-supplied order
// (which for these charts is value-desc, so the bigger slice keeps the
// hashed color and the smaller one bumps). No persistent state, so
// nothing breaks if a future page-by-page fetcher never sees all of
// history at once.
//
// Trade-off: when two categories that hash to the same slot are both
// visible, one of them gets bumped to a non-preferred slot, and which
// one wins depends on the input order. So a category's color can shift
// between windows if its "rival" appears in some windows but not
// others. With the 10-color palette and typical page sizes, this is
// uncommon; everything else stays stable.

export const SLICE_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c',
  '#d0ed57', '#83a6ed', '#8dd1e1', '#d88884', '#a28ddc',
];

export const OTHER_COLOR = '#bbbbbb';

// Yes/no (bool) features. Not categorical slots: Yes is the honey the activity
// ramp is drawn in, No a neutral stone, and "not recorded" the ramp's empty
// cell, so an unanswered day reads as blank rather than as a third answer.
// On the dataviz validator Yes↔No clear CVD ΔE 14 (protan) and No↔not recorded
// ΔE 21; the two stones fail its categorical chroma floor by design. Both fills
// sit under 3:1 against white, so every chart using them carries a text legend
// and per-mark tooltips.
export const BOOL_COLORS = {
  yes: '#f59e0b',      // amber-500
  no: '#a8a29e',       // stone-400
  unknown: '#e7e5e4',  // stone-200 — ActivityCalendar's empty cell
} as const;

// FNV-1a 32-bit. Deterministic across runs and platforms.
function hashSlot(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % SLICE_COLORS.length;
}

// Assign a palette color per input key. Keys earlier in the array win
// collisions (pass keys in value-desc order so the biggest slice keeps
// its hashed color).
export function assignSliceColors(keys: string[]): string[] {
  const taken = new Array<boolean>(SLICE_COLORS.length).fill(false);
  const out = new Array<string>(keys.length);
  for (let i = 0; i < keys.length; i++) {
    const preferred = hashSlot(keys[i]);
    let slot = preferred;
    while (taken[slot]) {
      slot = (slot + 1) % SLICE_COLORS.length;
      if (slot === preferred) break; // palette full — overlap is unavoidable
    }
    taken[slot] = true;
    out[i] = SLICE_COLORS[slot];
  }
  return out;
}
